import { useCallback, forwardRef, useRef, useEffect, useMemo, lazy, Suspense } from 'react'
import { useSnapshot } from 'valtio'
import { store } from '@/store'
import { motion, AnimatePresence } from 'framer-motion'
import Fuse from 'fuse.js'
import { TaskListView } from './list-view'
// Lazy load heavy view components
const KanbanView = lazy(() =>
  import('./kanban-view').then((module) => ({ default: module.KanbanView })),
)
const CalendarView = lazy(() =>
  import('./calendar-view').then((module) => ({ default: module.CalendarView })),
)
import { ViewSwitcher } from './view-switcher'
import { BottomNav } from './bottom-nav'
import { SelectionArea } from '@viselect/react'
import {
  Search,
  Filter,
  ArrowUpDown,
  X,
  Clock,
  AlertCircle,
  Calendar,
  Zap,
  CheckCircle2,
  LayoutPanelLeft,
  ListFilter,
  Check,
  Circle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { Kbd, KbdGroup } from '@/components/ui/kbd'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { smoothSpring, viewPresence } from './motion-presets'
import { useIsTouchCapable } from '@/hooks/use-touch-capable'
import { useTaskAutocomplete } from './hooks/use-task-autocomplete'
import { Skeleton } from '@/components/ui/skeleton'

const CalendarSkeleton = () => (
  <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-background">
    {/* Header matching CalendarHeader */}
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/10 min-h-[56px] bg-card">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-16 rounded-lg" />
        <Skeleton className="h-8 w-40 rounded-lg" />
        <Skeleton className="h-8 w-16 rounded-lg" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-32 rounded-xl" />
      </div>
    </div>

    {/* Day Header Row matching CalendarWeekHeaderRow */}
    <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-border/10 bg-muted/35">
      <div className="border-r border-border/10" />
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col items-center justify-center py-2.5 space-y-1 border-r border-border/10 last:border-r-0"
        >
          <Skeleton className="h-3 w-8 rounded" />
          <Skeleton className="h-4 w-6 rounded" />
        </div>
      ))}
    </div>

    {/* Calendar Body matching InteractiveCalendarTimeline */}
    <div className="flex-1 flex overflow-hidden min-h-0 relative">
      {/* Time Axis */}
      <div className="w-[56px] border-r border-border/10 bg-muted/10 flex flex-col pt-4 space-y-12 items-center shrink-0">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-2 w-8 rounded opacity-40" />
        ))}
      </div>

      {/* Day Columns */}
      <div className="flex-1 grid grid-cols-7 relative overflow-hidden h-full">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="border-r border-border/10 last:border-r-0 relative h-full">
            {/* Random task skeletons for variety */}
            {i % 2 === 0 && (
              <div className="absolute top-[10%] left-1 right-1">
                <Skeleton className="h-16 w-full rounded-lg opacity-20" />
              </div>
            )}
            {i % 3 === 0 && (
              <div className="absolute top-[40%] left-1 right-1">
                <Skeleton className="h-24 w-full rounded-lg opacity-15" />
              </div>
            )}
            {i % 5 === 0 && (
              <div className="absolute top-[70%] left-1 right-1">
                <Skeleton className="h-12 w-full rounded-lg opacity-10" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
)

const KanbanSkeleton = () => (
  <div className="flex-1 flex p-4 space-x-4 overflow-x-auto overflow-y-hidden custom-scrollbar bg-background">
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className="w-80 flex-shrink-0 flex flex-col h-full bg-muted/30 rounded-xl border border-border/10 p-4 space-y-4"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-3 rounded-full" />
            <Skeleton className="h-4 w-20 rounded" />
            <Skeleton className="h-4 w-6 rounded-full" />
          </div>
          <Skeleton className="h-6 w-6 rounded-full" />
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
        <div className="flex-1 space-y-3 overflow-hidden">
          {Array.from({ length: 3 }).map((_, j) => (
            <div key={j} className="p-3 bg-card border border-border/10 rounded-xl space-y-3">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 flex-1 rounded" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-3 w-12 rounded" />
                <Skeleton className="h-3 w-12 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

const ListSkeleton = () => (
  <div className="flex-1 p-4 sm:p-6 space-y-10 max-w-[40rem] mx-auto w-full bg-background">
    {/* Todo Section */}
    <div className="space-y-4">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Skeleton className="h-3 w-3 rounded-full" />
          <Skeleton className="h-5 w-24 rounded" />
          <Skeleton className="h-4 w-6 rounded-full" />
        </div>
        <Skeleton className="h-6 w-6 rounded-full" />
      </div>
      <Skeleton className="h-10 w-full rounded-xl" />
      <div className="space-y-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-3 py-2 px-2">
            <Skeleton className="h-5 w-5 rounded-md" />
            <Skeleton className="h-4 flex-1 rounded" />
            <Skeleton className="h-4 w-16 rounded" />
          </div>
        ))}
      </div>
    </div>

    {/* Done Section */}
    <div className="space-y-4 opacity-50">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Skeleton className="h-3 w-3 rounded-full" />
          <Skeleton className="h-5 w-24 rounded" />
        </div>
      </div>
      <div className="space-y-1">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-3 py-2 px-2">
            <Skeleton className="h-5 w-5 rounded-md" />
            <Skeleton className="h-4 flex-1 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
)

const STATUS_SCOPE_OPTIONS = [
  {
    value: 'all',
    label: 'All',
    icon: ListFilter,
    color: 'text-muted-foreground/60',
  },
  {
    value: 'todo',
    label: 'Todo',
    icon: Circle,
    color: 'text-blue-500',
  },
  {
    value: 'done',
    label: 'Done',
    icon: CheckCircle2,
    color: 'text-emerald-500',
  },
]
const FilterMenuItem = ({ value, label, icon: Icon, current, onSelect, className }) => {
  const active = current === value
  const handleClick = useCallback(() => {
    onSelect(value)
  }, [onSelect, value])
  return (
    <DropdownMenuItem
      onClick={handleClick}
      className={cn(
        'group flex items-center justify-between gap-2',
        active && 'bg-accent text-accent-foreground font-medium',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className={cn('h-4 w-4', value === 'overdue' && 'text-destructive')} />
        <span>{label}</span>
      </div>
      {active && <Check className="h-4 w-4 text-primary" />}
    </DropdownMenuItem>
  )
}
const SortMenuItem = ({ value, label, icon: Icon, current, onSelect }) => {
  const active = current === value
  const handleClick = useCallback(() => {
    onSelect(value)
  }, [onSelect, value])
  return (
    <DropdownMenuItem
      onClick={handleClick}
      className={cn(
        'group flex items-center justify-between gap-2',
        active && 'bg-accent text-accent-foreground font-medium',
      )}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <span>{label}</span>
      </div>
      {active && <Check className="h-4 w-4 text-primary" />}
    </DropdownMenuItem>
  )
}
const SidebarToggleHint = () => (
  <div className="flex items-center gap-2">
    Menu
    <KbdGroup>
      <Kbd>M</Kbd>
    </KbdGroup>
  </div>
)
const SidebarToggleButton = ({ showSidebar, handleToggleSidebar }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="ghost" size="sm" onClick={handleToggleSidebar} className="h-9 px-2">
        <LayoutPanelLeft
          className={cn('h-4 w-4 transition-transform', !showSidebar && 'rotate-180')}
        />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom" align="start">
      <SidebarToggleHint />
    </TooltipContent>
  </Tooltip>
)
const SearchField = ({
  isMobile,
  searchQuery,
  searchInputRef,
  handleSearchChange,
  handleClearSearch,
  searchCompletion,
  acceptSearchCompletion,
}) => {
  const onKeyDown = (e) => {
    if (e.key === 'Tab' && searchCompletion) {
      e.preventDefault()
      const nextText = acceptSearchCompletion(searchQuery)
      handleSearchChange({ target: { value: nextText } })
    }
  }

  return (
    <motion.div
      layout="position"
      className={cn(
        'relative group w-full transition-[opacity,transform] duration-300',
        isMobile ? 'max-w-none' : 'max-w-xs',
      )}
    >
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
      <div className="relative">
        {searchCompletion && (
          <div className="pointer-events-none absolute inset-0 z-0 pl-9 pr-9 py-2 text-sm leading-tight whitespace-pre overflow-hidden">
            <span className="opacity-0">{searchQuery}</span>
            <span className="text-muted-foreground/50">{searchCompletion}</span>
          </div>
        )}
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={onKeyDown}
          className={cn(
            'relative z-10 bg-muted/20 border border-border/5 focus-visible:ring-1 focus-visible:ring-primary/20 h-9 text-sm transition-[opacity,transform]',
            isMobile ? 'pl-8 pr-8' : 'pl-9 pr-9',
          )}
        />
      </div>
      {!searchQuery && !isMobile && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none z-20">
          <Kbd>/</Kbd>
        </div>
      )}
      <AnimatePresence initial={false}>
        {searchQuery && (
          <motion.div
            key="clear-search"
            initial={{
              opacity: 0,
              x: 6,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            exit={{
              opacity: 0,
              x: 6,
            }}
            transition={{
              duration: 0.16,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-20"
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-50 hover:opacity-100"
              onClick={handleClearSearch}
            >
              <X className="h-3 w-3" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
const StatusScopeSelect = ({ statusScope, onStatusScopeChange, isMobile, counts }) => {
  const currentOption =
    STATUS_SCOPE_OPTIONS.find((o) => o.value === statusScope) || STATUS_SCOPE_OPTIONS[0]
  const Icon = currentOption.icon

  return (
    <motion.div layout="position" transition={smoothSpring}>
      <Select value={statusScope} onValueChange={onStatusScopeChange}>
        <SelectTrigger
          size="sm"
          aria-label="Status scope"
          className={cn(
            'h-9 border-none bg-muted/30 focus-visible:ring-1 focus-visible:ring-primary/20',
            isMobile ? 'min-w-[84px] px-2' : 'min-w-[124px]',
            'w-auto',
          )}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Icon className={cn('h-4 w-4 shrink-0', currentOption.color)} />
            <SelectValue placeholder="All" className="flex-1" />
          </div>
        </SelectTrigger>
        <SelectContent align="start">
          {STATUS_SCOPE_OPTIONS.map((option) => {
            const OptionIcon = option.icon
            return (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex w-full items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <OptionIcon className={cn('h-3.5 w-3.5', option.color)} />
                    <span>{option.label}</span>
                  </div>
                  <span className="text-muted-foreground tabular-nums">
                    {counts[option.value] ?? 0}
                  </span>
                </div>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </motion.div>
  )
}
const FilterHint = () => (
  <div className="flex items-center gap-2">
    Filter
    <KbdGroup>
      <Kbd>F</Kbd>
    </KbdGroup>
  </div>
)
const SortHint = () => (
  <div className="flex items-center gap-2">
    Sort
    <KbdGroup>
      <Kbd>S</Kbd>
    </KbdGroup>
  </div>
)
const FilterTriggerButton = forwardRef(({ className, children, ...props }, ref) => (
  <Button
    ref={ref}
    variant="ghost"
    size="sm"
    className={cn('h-9 px-2 sm:px-3 gap-2 transition-[opacity,transform]', className)}
    {...props}
  >
    {children}
  </Button>
))
FilterTriggerButton.displayName = 'FilterTriggerButton'
const FilterDropdownTriggerButton = ({ filterBy }) => (
  <DropdownMenuTrigger asChild>
    <FilterTriggerButton className={filterBy ? 'text-primary bg-primary/5' : undefined}>
      <Filter className="h-4 w-4" />
      <span className="hidden sm:inline">
        {filterBy ? filterBy.charAt(0).toUpperCase() + filterBy.slice(1) : 'Filter'}
      </span>
    </FilterTriggerButton>
  </DropdownMenuTrigger>
)
const FilterDropdownTrigger = ({ filterBy, isMobile }) => {
  if (isMobile) {
    return <FilterDropdownTriggerButton filterBy={filterBy} />
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <FilterDropdownTriggerButton filterBy={filterBy} />
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <FilterHint />
      </TooltipContent>
    </Tooltip>
  )
}
const SortTriggerButton = forwardRef(({ className, children, ...props }, ref) => (
  <Button
    ref={ref}
    variant="ghost"
    size="sm"
    className={cn('h-9 px-2 sm:px-3 gap-2 transition-[opacity,transform]', className)}
    {...props}
  >
    {children}
  </Button>
))
SortTriggerButton.displayName = 'SortTriggerButton'
const SortDropdownTriggerButton = ({ sortBy }) => (
  <DropdownMenuTrigger asChild>
    <SortTriggerButton className={sortBy ? 'text-primary bg-primary/5' : undefined}>
      <ArrowUpDown className="h-4 w-4" />
      <span className="hidden sm:inline">
        {sortBy ? sortBy.charAt(0).toUpperCase() + sortBy.slice(1) : 'Sort'}
      </span>
    </SortTriggerButton>
  </DropdownMenuTrigger>
)
const SortDropdownTrigger = ({ sortBy, isMobile }) => {
  if (isMobile) {
    return <SortDropdownTriggerButton sortBy={sortBy} />
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <SortDropdownTriggerButton sortBy={sortBy} />
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <SortHint />
      </TooltipContent>
    </Tooltip>
  )
}
const FilterDropdown = ({
  isFilterOpen,
  setIsFilterOpen,
  filterBy,
  onFilterSelect,
  onFilterClear,
  isMobile,
}) => (
  <DropdownMenu open={isFilterOpen} onOpenChange={setIsFilterOpen}>
    <FilterDropdownTrigger filterBy={filterBy} isMobile={isMobile} />
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuLabel>Show only</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <FilterMenuItem
        value="today"
        label="Today"
        icon={Clock}
        current={filterBy}
        onSelect={onFilterSelect}
      />
      <FilterMenuItem
        value="tomorrow"
        label="Tomorrow"
        icon={Calendar}
        current={filterBy}
        onSelect={onFilterSelect}
      />
      <FilterMenuItem
        value="overdue"
        label="Overdue"
        icon={AlertCircle}
        current={filterBy}
        onSelect={onFilterSelect}
      />
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={onFilterClear}
        className={cn(
          'group flex items-center justify-between gap-2',
          !filterBy && 'bg-accent text-accent-foreground font-medium',
        )}
      >
        <div className="flex items-center gap-2 text-muted-foreground group-hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
          <span>Reset</span>
        </div>
        {!filterBy && <Check className="h-4 w-4 text-primary" />}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)
const SortDropdown = ({
  isViewOpen,
  setIsViewOpen,
  sortBy,
  onSortSelect,
  onSortClear,
  isMobile,
}) => (
  <DropdownMenu open={isViewOpen} onOpenChange={setIsViewOpen}>
    <SortDropdownTrigger sortBy={sortBy} isMobile={isMobile} />
    <DropdownMenuContent align="end" className="w-48">
      <DropdownMenuLabel>Order by</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <SortMenuItem
        value="date"
        label="Date"
        icon={Calendar}
        current={sortBy}
        onSelect={onSortSelect}
      />
      <SortMenuItem
        value="priority"
        label="Priority"
        icon={Zap}
        current={sortBy}
        onSelect={onSortSelect}
      />
      <SortMenuItem
        value="status"
        label="Status"
        icon={CheckCircle2}
        current={sortBy}
        onSelect={onSortSelect}
      />
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={onSortClear}
        className={cn(
          'group flex items-center justify-between gap-2',
          !sortBy && 'bg-accent text-accent-foreground font-medium',
        )}
      >
        <div className="flex items-center gap-2 text-muted-foreground group-hover:text-foreground transition-colors">
          <X className="h-4 w-4" />
          <span>Default</span>
        </div>
        {!sortBy && <Check className="h-4 w-4 text-primary" />}
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)
const FilterSortControls = ({
  isFilterOpen,
  setIsFilterOpen,
  filterBy,
  onFilterSelect,
  onFilterClear,
  isViewOpen,
  setIsViewOpen,
  sortBy,
  onSortSelect,
  onSortClear,
  isMobile,
}) => (
  <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
    <div className="inline-flex">
      <FilterDropdown
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        filterBy={filterBy}
        onFilterSelect={onFilterSelect}
        onFilterClear={onFilterClear}
        isMobile={isMobile}
      />
    </div>
    <div className="inline-flex">
      <SortDropdown
        isViewOpen={isViewOpen}
        setIsViewOpen={setIsViewOpen}
        sortBy={sortBy}
        onSortSelect={onSortSelect}
        onSortClear={onSortClear}
        isMobile={isMobile}
      />
    </div>
  </div>
)
const TaskViewToolbar = ({
  isMobile,
  activeTab,
  uiSettings,
  showSidebar,
  handleToggleSidebar,
  statusScope,
  onStatusScopeChange,
  statusScopeCounts,
  searchQuery,
  searchInputRef,
  handleSearchChange,
  handleClearSearch,
  searchCompletion,
  acceptSearchCompletion,
  handleViewSelect,
  isFilterOpen,
  setIsFilterOpen,
  filterBy,
  onFilterSelect,
  onFilterClear,
  isViewOpen,
  setIsViewOpen,
  sortBy,
  onSortSelect,
  onSortClear,
}) => (
  <div className={cn('flex w-full min-w-0 items-center', isMobile ? 'gap-1' : 'gap-2')}>
    <div className="flex flex-1 items-center gap-1.5 min-w-0 justify-start">
      <StatusScopeSelect
        statusScope={statusScope}
        onStatusScopeChange={onStatusScopeChange}
        isMobile={isMobile}
        counts={statusScopeCounts}
      />
    </div>

    <div className="flex-1 flex items-center justify-center min-w-0">
      {uiSettings.showSearch && (
        <SearchField
          isMobile={isMobile}
          searchQuery={searchQuery}
          searchInputRef={searchInputRef}
          handleSearchChange={handleSearchChange}
          handleClearSearch={handleClearSearch}
          searchCompletion={searchCompletion}
          acceptSearchCompletion={acceptSearchCompletion}
        />
      )}
    </div>

    <div
      className={cn(
        'flex items-center gap-1 min-w-0 justify-end',
        isMobile ? 'shrink-0' : 'flex-1',
        !uiSettings?.showRightPanel && 'pr-12',
      )}
    >
      {uiSettings.showViewFilter && (
        <FilterSortControls
          isFilterOpen={isFilterOpen}
          setIsFilterOpen={setIsFilterOpen}
          filterBy={filterBy}
          onFilterSelect={onFilterSelect}
          onFilterClear={onFilterClear}
          isViewOpen={isViewOpen}
          setIsViewOpen={setIsViewOpen}
          sortBy={sortBy}
          onSortSelect={onSortSelect}
          onSortClear={onSortClear}
          isMobile={isMobile}
        />
      )}
    </div>
  </div>
)
const TaskViewTopBar = ({
  isMobile,
  isTinyScreen,
  activeTab,
  uiSettings,
  showSidebar,
  handleToggleSidebar,
  statusScope,
  onStatusScopeChange,
  statusScopeCounts,
  searchQuery,
  searchInputRef,
  handleSearchChange,
  handleClearSearch,
  searchCompletion,
  acceptSearchCompletion,
  handleViewSelect,
  isFilterOpen,
  setIsFilterOpen,
  filterBy,
  onFilterSelect,
  onFilterClear,
  isViewOpen,
  setIsViewOpen,
  sortBy,
  onSortSelect,
  onSortClear,
  loadItem,
}) => (
  <motion.div
    layout="position"
    transition={smoothSpring}
    className="w-full sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border/10 h-[52px] flex flex-col justify-center gap-0"
  >
    <div className={cn('mx-auto w-full', isTinyScreen ? 'px-1' : isMobile ? 'px-2' : 'px-4')}>
      <motion.div variants={loadItem} className="w-full">
        <div className={cn(!showSidebar && 'pl-16')}>
          <TaskViewToolbar
            isMobile={isMobile}
            activeTab={activeTab}
            uiSettings={uiSettings}
            showSidebar={showSidebar}
            handleToggleSidebar={handleToggleSidebar}
            statusScope={statusScope}
            onStatusScopeChange={onStatusScopeChange}
            statusScopeCounts={statusScopeCounts}
            searchQuery={searchQuery}
            searchInputRef={searchInputRef}
            handleSearchChange={handleSearchChange}
            handleClearSearch={handleClearSearch}
            searchCompletion={searchCompletion}
            acceptSearchCompletion={acceptSearchCompletion}
            handleViewSelect={handleViewSelect}
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            filterBy={filterBy}
            onFilterSelect={onFilterSelect}
            onFilterClear={onFilterClear}
            isViewOpen={isViewOpen}
            setIsViewOpen={setIsViewOpen}
            sortBy={sortBy}
            onSortSelect={onSortSelect}
            onSortClear={onSortClear}
          />
        </div>
      </motion.div>
    </div>
  </motion.div>
)
const NoResultsState = ({ handleClearSearch }) => (
  <motion.div
    key="no-results"
    layout="position"
    {...viewPresence}
    transition={smoothSpring}
    className="flex-1 flex flex-col items-center justify-center py-20 text-center"
  >
    <h3 className="text-xl font-bold mb-2">No tasks found</h3>
    <button onClick={handleClearSearch}>Reset</button>
  </motion.div>
)
const CalendarTab = ({
  selectedListId,
  todos,
  lists,
  searchQuery,
  statusScope,
  handleItemSelect,
  updateTodo,
  handleQuickAdd,
  weatherUnit,
  uiSettings,
  toggleTodo,
  deleteTodo,
  duplicateTodo,
  handleToggleSidebar,
  showSidebar,
  handleToggleAllExpanded,
  anyExpanded,
  handleToggleAllDone,
  hasTodoTasks,
  hasDoneTasks,
  handleToggleVisibility,
  sortBy,
  handleSortChange,
  handleClearSort,
  filterBy,
  handleFilterChange,
  handleClearFilter,
}) => {
  const calendarTodos = useMemo(() => {
    let items = [...todos]
    if (selectedListId !== 'all') {
      items = items.filter((t) => t.listId === selectedListId)
    }

    if (statusScope !== 'all') {
      items = items.filter((t) => t.status === statusScope)
    }

    if (searchQuery.trim()) {
      const fuse = new Fuse(items, {
        keys: ['text', 'description', 'subtasks.text'],
        threshold: 0.3,
      })
      items = fuse.search(searchQuery).map((result) => result.item)
    }

    return items
  }, [todos, selectedListId, statusScope, searchQuery])

  return (
    <motion.div
      key={`calendar-${selectedListId}`}
      layout="position"
      {...viewPresence}
      className="flex-1 h-full min-h-0 flex flex-col"
    >
      <CalendarView
        todos={calendarTodos}
        lists={lists}
        onSelectTodo={handleItemSelect}
        onUpdateTodo={updateTodo}
        onToggleTodo={toggleTodo}
        onDeleteTodo={deleteTodo}
        onDuplicateTodo={duplicateTodo}
        selectedListId={selectedListId}
        onQuickAdd={handleQuickAdd}
        weatherUnit={weatherUnit}
        uiSettings={uiSettings}
        handleToggleSidebar={handleToggleSidebar}
        showSidebar={showSidebar}
        handleToggleAllExpanded={handleToggleAllExpanded}
        anyExpanded={anyExpanded}
        handleToggleAllDone={handleToggleAllDone}
        hasTodoTasks={hasTodoTasks}
        hasDoneTasks={hasDoneTasks}
        handleToggleVisibility={handleToggleVisibility}
        sortBy={sortBy}
        handleSortChange={handleSortChange}
        handleClearSort={handleClearSort}
        filterBy={filterBy}
        handleFilterChange={handleFilterChange}
        handleClearFilter={handleClearFilter}
      />
    </motion.div>
  )
}
const KanbanTab = ({
  selectedListId,
  processedTodos,
  kanbanStatuses,
  itemsByStatus,
  selectedIds,
  editingId,
  setEditingId,
  toggleTodo,
  deleteTodo,
  updateTodo,
  handleItemSelect,
  duplicateTodo,
  recentlyDuplicatedIds,
  newTaskId,
  isSelecting,
  draggingIds,
  isDragActive,
  dragSizes,
  registerItem,
  sectionColors,
  sensors,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  resetDragState,
  handleCreateStart,
  activeTodo,
  activeDragSize,
  uiSettings,
  sortBy,
  handleSortChange,
  handleClearSort,
  filterBy,
  handleFilterChange,
  handleClearFilter,
  handleToggleSectionVisibility,
  handleToggleAllExpanded,
  handleToggleAllDone,
  anyExpanded,
  todosLength,
  hasTodoTasks,
  hasDoneTasks,
  handleToggleVisibility,
  handleToggleSidebar,
  showSidebar,
  handleQuickAdd,
}) => (
  <motion.div
    key={`kanban-${selectedListId}`}
    layout="position"
    {...viewPresence}
    className="flex-1"
  >
    <KanbanView
      todos={processedTodos}
      allStatuses={kanbanStatuses}
      itemsByStatus={itemsByStatus}
      selectedIds={selectedIds}
      editingId={editingId}
      setEditingId={setEditingId}
      onToggle={toggleTodo}
      onDelete={deleteTodo}
      onUpdate={updateTodo}
      onSelect={handleItemSelect}
      onDuplicate={duplicateTodo}
      recentlyDuplicatedIds={recentlyDuplicatedIds}
      newTaskId={newTaskId}
      isSelecting={isSelecting}
      draggingIds={draggingIds}
      isDragging={isDragActive}
      dragSizes={dragSizes}
      registerItem={registerItem}
      sectionColors={sectionColors}
      sensors={sensors}
      handleDragStart={handleDragStart}
      handleDragOver={handleDragOver}
      handleDragEnd={handleDragEnd}
      resetDragState={resetDragState}
      onCreateStart={handleCreateStart}
      onQuickAdd={handleQuickAdd}
      activeTodo={activeTodo}
      activeDragSize={activeDragSize}
      showListBadge={selectedListId === 'all'}
      uiSettings={uiSettings}
      sortBy={sortBy}
      handleSortChange={handleSortChange}
      handleClearSort={handleClearSort}
      filterBy={filterBy}
      handleFilterChange={handleFilterChange}
      handleClearFilter={handleClearFilter}
      handleToggleSectionVisibility={handleToggleSectionVisibility}
      handleToggleAllExpanded={handleToggleAllExpanded}
      handleToggleAllDone={handleToggleAllDone}
      anyExpanded={anyExpanded}
      todosLength={todosLength}
      hasTodoTasks={hasTodoTasks}
      hasDoneTasks={hasDoneTasks}
      handleToggleVisibility={handleToggleVisibility}
      handleToggleSidebar={handleToggleSidebar}
      showSidebar={showSidebar}
    />
  </motion.div>
)
const TaskViewBody = ({
  isMobile,
  isTinyScreen,
  activeTab,
  processedTodos,
  todos,
  lists,
  searchQuery,
  statusScope,
  handleClearSearch,
  selectedListId,
  kanbanStatuses,
  allStatuses,
  itemsByStatus,
  todoMap,
  uiSettings,
  sensors,
  collisionDetection,
  isDragActive,
  draggingIds,
  dragSizes,
  activeTodo,
  activeDragSize,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  handleDragCancel,
  resetDragState,
  registerItem,
  statusCreateHandlers,
  statusColorHandlers,
  statusQuickAddHandlers,
  toggleTodo,
  deleteTodo,
  updateTodo,
  handleItemSelect,
  duplicateTodo,
  setEditingId,
  setIsColorPickerOpen,
  selectedIds,
  editingId,
  recentlyDuplicatedIds,
  newTaskId,
  isSelecting,
  sectionColors,
  todoQuickAddRef,
  doneQuickAddRef,
  sortBy,
  handleSortChange,
  handleClearSort,
  filterBy,
  handleFilterChange,
  handleClearFilter,
  handleToggleAllExpanded,
  handleToggleAllDone,
  anyExpanded,
  hasTodoTasks,
  hasDoneTasks,
  todosLength,
  handleToggleVisibility,
  handleToggleSectionVisibility,
  handleTaskDragCondition,
  handleTaskSelectoStart,
  handleTaskSelecto,
  handleTaskSelectoEnd,
  handleQuickAdd,
  handleCreateStart,
  handleToggleSidebar,
  showSidebar,
  loadItem,
  scrollContainerRef,
  handleClearSelectedIds,
  weatherUnit,
}) => {
  // Refs for stable handlers
  const onStartRef = useRef(handleTaskSelectoStart)
  const onMoveRef = useRef(handleTaskSelecto)
  const onStopRef = useRef(handleTaskSelectoEnd)
  const onBeforeStartRef = useRef(handleTaskDragCondition)
  const blockedSelectionRef = useRef(false)
  const isTouchCapable = useIsTouchCapable()

  useEffect(() => {
    onStartRef.current = handleTaskSelectoStart
    onMoveRef.current = handleTaskSelecto
    onStopRef.current = handleTaskSelectoEnd
    onBeforeStartRef.current = handleTaskDragCondition
  }, [handleTaskSelectoStart, handleTaskSelecto, handleTaskSelectoEnd, handleTaskDragCondition])
  const onBeforeStart = useCallback(
    (e) => {
      const eventTarget = e.event?.target
      if (eventTarget instanceof Element && eventTarget.closest('[data-add-task]')) {
        blockedSelectionRef.current = true
        handleClearSelectedIds()
        return false
      }
      blockedSelectionRef.current = false
      return onBeforeStartRef.current?.({ inputEvent: e.event })
    },
    [handleClearSelectedIds],
  )
  const onStart = useCallback((e) => {
    if (blockedSelectionRef.current) return
    onStartRef.current?.({ inputEvent: e.event })
  }, [])
  const onMove = useCallback((e) => {
    if (blockedSelectionRef.current) return
    onMoveRef.current?.({
      ...e,
      selected: e.store.selected,
      added: e.store.changed.added,
      removed: e.store.changed.removed,
      inputEvent: e.event,
    })
  }, [])
  const onStop = useCallback((e) => {
    if (blockedSelectionRef.current) {
      blockedSelectionRef.current = false
      return
    }
    onStopRef.current?.({ inputEvent: e.event })
  }, [])

  const snap = useSnapshot(store)

  return (
    <motion.div
      layout="position"
      variants={loadItem}
      transition={smoothSpring}
      id="scroll-container"
      className={cn(
        'flex-1 min-h-0',
        activeTab === 'calendar' ? 'overflow-hidden' : 'overflow-y-auto',
      )}
    >
      <motion.div
        layout="position"
        transition={smoothSpring}
        ref={scrollContainerRef}
        className={cn(
          'h-full min-h-0 flex flex-col',
          activeTab === 'calendar' ? 'w-full pb-0' : 'container mx-auto pb-6',
          activeTab === 'calendar' ? 'px-0' : isTinyScreen ? 'px-1' : 'px-4',
          activeTab === 'kanban' || activeTab === 'calendar' ? 'max-w-full' : 'max-w-[40rem]',
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          {processedTodos.length === 0 && searchQuery ? (
            <NoResultsState handleClearSearch={handleClearSearch} />
          ) : snap.isLoading ? (
            activeTab === 'list' ? (
              <ListSkeleton key="list-skeleton" />
            ) : activeTab === 'calendar' ? (
              <CalendarSkeleton key="calendar-skeleton" />
            ) : activeTab === 'kanban' ? (
              <KanbanSkeleton key="kanban-skeleton" />
            ) : null
          ) : (
            <>
              {activeTab === 'list' && (
                <TaskListView
                  key={`list-${selectedListId}`}
                  isMobile={isMobile}
                  kanbanStatuses={kanbanStatuses}
                  allStatuses={allStatuses}
                  itemsByStatus={itemsByStatus}
                  todoMap={todoMap}
                  searchQuery={searchQuery}
                  statusScope={statusScope}
                  uiSettings={uiSettings}
                  sensors={sensors}
                  collisionDetection={collisionDetection}
                  isDragActive={isDragActive}
                  draggingIds={draggingIds}
                  dragSizes={dragSizes}
                  activeTodo={activeTodo}
                  activeDragSize={activeDragSize}
                  handleDragStart={handleDragStart}
                  handleDragOver={handleDragOver}
                  handleDragEnd={handleDragEnd}
                  handleDragCancel={handleDragCancel}
                  registerItem={registerItem}
                  statusCreateHandlers={statusCreateHandlers}
                  statusColorHandlers={statusColorHandlers}
                  statusQuickAddHandlers={statusQuickAddHandlers}
                  toggleTodo={toggleTodo}
                  deleteTodo={deleteTodo}
                  updateTodo={updateTodo}
                  handleItemSelect={handleItemSelect}
                  duplicateTodo={duplicateTodo}
                  setEditingId={setEditingId}
                  setIsColorPickerOpen={setIsColorPickerOpen}
                  selectedIds={selectedIds}
                  editingId={editingId}
                  recentlyDuplicatedIds={recentlyDuplicatedIds}
                  newTaskId={newTaskId}
                  isSelecting={isSelecting}
                  selectedListId={selectedListId}
                  sectionColors={sectionColors}
                  todoQuickAddRef={todoQuickAddRef}
                  doneQuickAddRef={doneQuickAddRef}
                  sortBy={sortBy}
                  handleSortChange={handleSortChange}
                  handleClearSort={handleClearSort}
                  filterBy={filterBy}
                  handleFilterChange={handleFilterChange}
                  handleClearFilter={handleClearFilter}
                  handleToggleAllExpanded={handleToggleAllExpanded}
                  handleToggleAllDone={handleToggleAllDone}
                  anyExpanded={anyExpanded}
                  hasTodoTasks={hasTodoTasks}
                  hasDoneTasks={hasDoneTasks}
                  todosLength={todosLength}
                  handleToggleVisibility={handleToggleVisibility}
                  handleToggleSectionVisibility={handleToggleSectionVisibility}
                  handleTaskDragCondition={handleTaskDragCondition}
                  handleTaskSelectoStart={handleTaskSelectoStart}
                  handleTaskSelecto={handleTaskSelecto}
                  handleTaskSelectoEnd={handleTaskSelectoEnd}
                  handleToggleSidebar={handleToggleSidebar}
                  showSidebar={showSidebar}
                  handleCreateStart={handleCreateStart}
                  scrollContainerRef={scrollContainerRef}
                  handleClearSelectedIds={handleClearSelectedIds}
                />
              )}

              {activeTab === 'calendar' && (
                <Suspense fallback={<CalendarSkeleton />}>
                  <CalendarTab
                    selectedListId={selectedListId}
                    todos={todos}
                    lists={lists}
                    searchQuery={searchQuery}
                    statusScope={statusScope}
                    handleItemSelect={handleItemSelect}
                    updateTodo={updateTodo}
                    toggleTodo={toggleTodo}
                    deleteTodo={deleteTodo}
                    duplicateTodo={duplicateTodo}
                    handleQuickAdd={handleQuickAdd}
                    weatherUnit={weatherUnit}
                    uiSettings={uiSettings}
                    handleToggleSidebar={handleToggleSidebar}
                    showSidebar={showSidebar}
                    handleToggleAllExpanded={handleToggleAllExpanded}
                    anyExpanded={anyExpanded}
                    handleToggleAllDone={handleToggleAllDone}
                    hasTodoTasks={hasTodoTasks}
                    hasDoneTasks={hasDoneTasks}
                    handleToggleVisibility={handleToggleVisibility}
                    sortBy={sortBy}
                    handleSortChange={handleSortChange}
                    handleClearSort={handleClearSort}
                    filterBy={filterBy}
                    handleFilterChange={handleFilterChange}
                    handleClearFilter={handleClearFilter}
                  />
                </Suspense>
              )}

              {activeTab === 'kanban' && (
                <Suspense fallback={<KanbanSkeleton />}>
                  <KanbanTab
                    selectedListId={selectedListId}
                    processedTodos={processedTodos}
                    kanbanStatuses={kanbanStatuses}
                    itemsByStatus={itemsByStatus}
                    selectedIds={selectedIds}
                    editingId={editingId}
                    setEditingId={setEditingId}
                    toggleTodo={toggleTodo}
                    onDelete={deleteTodo}
                    updateTodo={updateTodo}
                    handleItemSelect={handleItemSelect}
                    onDuplicate={duplicateTodo}
                    recentlyDuplicatedIds={recentlyDuplicatedIds}
                    newTaskId={newTaskId}
                    isSelecting={isSelecting}
                    draggingIds={draggingIds}
                    isDragActive={isDragActive}
                    dragSizes={dragSizes}
                    registerItem={registerItem}
                    sectionColors={sectionColors}
                    sensors={sensors}
                    handleDragStart={handleDragStart}
                    handleDragOver={handleDragOver}
                    handleDragEnd={handleDragEnd}
                    resetDragState={resetDragState}
                    handleCreateStart={handleCreateStart}
                    handleQuickAdd={handleQuickAdd}
                    activeTodo={activeTodo}
                    activeDragSize={activeDragSize}
                    uiSettings={uiSettings}
                    sortBy={sortBy}
                    handleSortChange={handleSortChange}
                    handleClearSort={handleClearSort}
                    filterBy={filterBy}
                    handleFilterChange={handleFilterChange}
                    handleClearFilter={handleClearFilter}
                    handleToggleSectionVisibility={handleToggleSectionVisibility}
                    handleToggleAllExpanded={handleToggleAllExpanded}
                    handleToggleAllDone={handleToggleAllDone}
                    anyExpanded={anyExpanded}
                    todosLength={todosLength}
                    hasTodoTasks={hasTodoTasks}
                    hasDoneTasks={hasDoneTasks}
                    handleToggleVisibility={handleToggleVisibility}
                    handleToggleSidebar={handleToggleSidebar}
                    showSidebar={showSidebar}
                  />
                </Suspense>
              )}
            </>
          )}
        </AnimatePresence>
        {!isTouchCapable && (
          <SelectionArea
            boundaries="#scroll-container"
            selectables=".task-item"
            selectionAreaClass="selection-area"
            behaviour={{
              overlap: 'keep',
              intersect: 'touch',
              startThreshold: 10,
              scrolling: {
                speedDivider: 10,
                manualSpeed: 750,
                startScrollMargins: { x: 0, y: 0 },
              },
            }}
            features={{
              touch: true,
              range: true,
              singleTap: { allow: false },
            }}
            onBeforeStart={onBeforeStart}
            onStart={onStart}
            onMove={onMove}
            onStop={onStop}
          />
        )}
      </motion.div>
    </motion.div>
  )
}
export function TaskViewSection({
  isMobile,
  isTinyScreen,
  activeTab,
  handleViewSelect,
  processedTodos,
  statusScope,
  setStatusScope,
  searchQuery,
  setSearchQuery,
  kanbanStatuses,
  allStatuses,
  itemsByStatus,
  todoMap,
  uiSettings,
  sensors,
  collisionDetection,
  isDragActive,
  draggingIds,
  dragSizes,
  activeTodo,
  activeDragSize,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  resetDragState,
  registerItem,
  statusCreateHandlers,
  statusColorHandlers,
  statusQuickAddHandlers,
  toggleTodo,
  deleteTodo,
  updateTodo,
  handleItemSelect,
  duplicateTodo,
  setEditingId,
  setIsColorPickerOpen,
  selectedIds,
  editingId,
  recentlyDuplicatedIds,
  newTaskId,
  isSelecting,
  selectedListId,
  sectionColors,
  todoQuickAddRef,
  doneQuickAddRef,
  sortBy,
  handleSortChange,
  handleClearSort,
  filterBy,
  handleFilterChange,
  handleClearFilter,
  handleToggleAllExpanded,
  handleToggleAllDone,
  anyExpanded,
  hasTodoTasks,
  hasDoneTasks,
  todosLength,
  handleToggleVisibility,
  handleToggleSectionVisibility,
  handleTaskDragCondition,
  handleTaskSelectoStart,
  handleTaskSelecto,
  handleTaskSelectoEnd,
  handleQuickAdd,
  handleCreateStart,
  isViewOpen,
  setIsViewOpen,
  isFilterOpen,
  setIsFilterOpen,
  handleToggleSidebar,
  showSidebar,
  searchInputRef,
  loadItem,
  scrollContainerRef,
  handleClearSelectedIds,
  todos,
  lists,
  setSelectedListId,
  weatherUnit,
}) {
  const statusScopeCounts = useMemo(() => {
    let todo = 0
    let done = 0
    for (let i = 0; i < todos.length; i += 1) {
      const status = todos[i]?.status
      if (status === 'todo') todo += 1
      if (status === 'done') done += 1
    }
    return {
      all: todos.length,
      todo,
      done,
    }
  }, [todos])

  const { completion: searchCompletion, acceptCompletion: acceptSearchCompletion } =
    useTaskAutocomplete(searchQuery, uiSettings.showSearch, { type: 'search' })

  const handleSearchChange = useCallback(
    (e) => {
      setSearchQuery(e.target.value)
    },
    [setSearchQuery],
  )
  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
  }, [setSearchQuery])
  const handleStatusScopeChange = useCallback(
    (scope) => {
      if (scope === 'all' || scope === 'todo' || scope === 'done') {
        setStatusScope(scope)
      }
    },
    [setStatusScope],
  )
  const onFilterSelect = useCallback(
    (val) => {
      handleFilterChange(val)
      setIsFilterOpen(false)
    },
    [handleFilterChange, setIsFilterOpen],
  )
  const onFilterClear = useCallback(() => {
    handleClearFilter()
    setIsFilterOpen(false)
  }, [handleClearFilter, setIsFilterOpen])
  const onSortSelect = useCallback(
    (val) => {
      handleSortChange(val)
      setIsViewOpen(false)
    },
    [handleSortChange, setIsViewOpen],
  )
  const onSortClear = useCallback(() => {
    handleClearSort()
    setIsViewOpen(false)
  }, [handleClearSort, setIsViewOpen])
  const handleDragCancelWrapper = useCallback(() => resetDragState(true), [resetDragState])

  const handleInboxClick = useCallback(() => {
    setSelectedListId('all')
  }, [setSelectedListId])

  return (
    <>
      <TaskViewTopBar
        isMobile={isMobile}
        isTinyScreen={isTinyScreen}
        activeTab={activeTab}
        uiSettings={uiSettings}
        showSidebar={showSidebar}
        handleToggleSidebar={handleToggleSidebar}
        statusScope={statusScope}
        onStatusScopeChange={handleStatusScopeChange}
        statusScopeCounts={statusScopeCounts}
        searchQuery={searchQuery}
        searchInputRef={searchInputRef}
        handleSearchChange={handleSearchChange}
        handleClearSearch={handleClearSearch}
        searchCompletion={searchCompletion}
        acceptSearchCompletion={acceptSearchCompletion}
        handleViewSelect={handleViewSelect}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        filterBy={filterBy}
        onFilterSelect={onFilterSelect}
        onFilterClear={onFilterClear}
        isViewOpen={isViewOpen}
        setIsViewOpen={setIsViewOpen}
        sortBy={sortBy}
        onSortSelect={onSortSelect}
        onSortClear={onSortClear}
        loadItem={loadItem}
      />
      <TaskViewBody
        isMobile={isMobile}
        isTinyScreen={isTinyScreen}
        activeTab={activeTab}
        processedTodos={processedTodos}
        todos={todos}
        lists={lists}
        searchQuery={searchQuery}
        statusScope={statusScope}
        handleClearSearch={handleClearSearch}
        selectedListId={selectedListId}
        kanbanStatuses={kanbanStatuses}
        allStatuses={allStatuses}
        itemsByStatus={itemsByStatus}
        todoMap={todoMap}
        uiSettings={uiSettings}
        sensors={sensors}
        collisionDetection={collisionDetection}
        isDragActive={isDragActive}
        draggingIds={draggingIds}
        dragSizes={dragSizes}
        activeTodo={activeTodo}
        activeDragSize={activeDragSize}
        handleDragStart={handleDragStart}
        handleDragOver={handleDragOver}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancelWrapper}
        resetDragState={resetDragState}
        registerItem={registerItem}
        statusCreateHandlers={statusCreateHandlers}
        statusColorHandlers={statusColorHandlers}
        statusQuickAddHandlers={statusQuickAddHandlers}
        toggleTodo={toggleTodo}
        deleteTodo={deleteTodo}
        updateTodo={updateTodo}
        handleItemSelect={handleItemSelect}
        duplicateTodo={duplicateTodo}
        setEditingId={setEditingId}
        setIsColorPickerOpen={setIsColorPickerOpen}
        selectedIds={selectedIds}
        editingId={editingId}
        recentlyDuplicatedIds={recentlyDuplicatedIds}
        newTaskId={newTaskId}
        isSelecting={isSelecting}
        sectionColors={sectionColors}
        todoQuickAddRef={todoQuickAddRef}
        doneQuickAddRef={doneQuickAddRef}
        sortBy={sortBy}
        handleSortChange={handleSortChange}
        handleClearSort={handleClearSort}
        filterBy={filterBy}
        handleFilterChange={handleFilterChange}
        handleClearFilter={handleClearFilter}
        handleToggleAllExpanded={handleToggleAllExpanded}
        handleToggleAllDone={handleToggleAllDone}
        anyExpanded={anyExpanded}
        hasTodoTasks={hasTodoTasks}
        hasDoneTasks={hasDoneTasks}
        todosLength={todosLength}
        handleToggleVisibility={handleToggleVisibility}
        handleToggleSectionVisibility={handleToggleSectionVisibility}
        handleTaskDragCondition={handleTaskDragCondition}
        handleTaskSelectoStart={handleTaskSelectoStart}
        handleTaskSelecto={handleTaskSelecto}
        handleTaskSelectoEnd={handleTaskSelectoEnd}
        handleCreateStart={handleCreateStart}
        handleQuickAdd={handleQuickAdd}
        handleToggleSidebar={handleToggleSidebar}
        showSidebar={showSidebar}
        loadItem={loadItem}
        scrollContainerRef={scrollContainerRef}
        handleClearSelectedIds={handleClearSelectedIds}
        weatherUnit={weatherUnit}
      />
      <BottomNav
        activeTab={activeTab}
        handleViewSelect={handleViewSelect}
        handleToggleSidebar={handleToggleSidebar}
        handleInboxClick={handleInboxClick}
      />
    </>
  )
}
